import { List, Record } from "immutable";

import type { IAccessReviewPermissionsStatistics } from "./AccessReviewModel";

import { AccessReviewResourceModel } from "./AccessReviewResourceModel";
import { AccessReviewSubordinateModel } from "./AccessReviewSubordinateModel";

export interface IReportItem {
  id: string;
  reportItemId: string;
}
export type TAccessReviewReportStatus = "done" | "inProgress" | "pending";

const defaults = {
  accessReviewId: "",
  createdAt: new Date(0),
  id: "",
  permissionCount: null as null | number,
  resources: null as List<AccessReviewResourceModel> | null,
  sortedResources: null as IReportItem[] | null,
  sortedSubordinates: null as IReportItem[] | null,
  statistics: null as IAccessReviewPermissionsStatistics | null,
  status: undefined as TAccessReviewReportStatus | undefined,
  subordinates: null as List<AccessReviewSubordinateModel> | null,
  userId: "",
};

export class AccessReviewReportModel extends Record<typeof defaults>(defaults) {
  public get calculatedStatus(): TAccessReviewReportStatus {
    if (this.status) {
      return this.status;
    }
    if (this.statistics?.permissionsStatus) {
      if (this.statistics.permissionsStatus.pending === this.permissionsSize) return "pending";
      if (this.statistics.permissionsStatus.pending === 0) return "done";
      return "inProgress";
    }
    if (this.subordinates || this.resources) {
      const subordinateStatuses = this.subordinates?.map((subordinate) => subordinate.status) || [];
      const resourceStatuses = this.resources?.map((resource) => resource.status) || [];
      const statuses = [...subordinateStatuses, ...resourceStatuses];
      if (statuses.includes("inProgress")) return "inProgress";
      if (statuses.every((status) => status === "done")) return "done";
    }
    return "pending";
  }

  public get permissionsSize(): number {
    if (this.permissionCount) return this.permissionCount;
    if (this.statistics?.permissionsStatus) {
      return Object.values(this.statistics.permissionsStatus).reduce((a, b) => a + b, 0);
    }
    return 0;
  }

  static fromServerData(data: unknown): AccessReviewReportModel {
    const {
      accessReviewId,
      createdAt,
      id,
      permissionCount,
      resources,
      sortedResources,
      sortedSubordinates,
      statistics,
      status,
      subordinates,
      userId,
    } = data as typeof defaults;
    return new AccessReviewReportModel({
      accessReviewId,
      createdAt: new Date(createdAt),
      id,
      permissionCount,
      resources: resources ? List(resources.map(AccessReviewResourceModel.fromServerData)) : null,
      sortedResources: sortedResources || [],
      sortedSubordinates: sortedSubordinates || [],
      statistics,
      status,
      subordinates: subordinates ? List(subordinates.map(AccessReviewSubordinateModel.fromServerData)) : null,
      userId,
    });
  }
}
